import { useEffect, useState } from "react";
import {
  Button,
  CardContent,
  CardHeader,
  Dialog,
  DialogActions,
  DialogContent,
  Loading,
  Step,
  StepLabel,
  Stepper,
} from "../../../../components";
import { InputErros } from "../../../../helpers";
import {
  EtiquetasService,
  notification,
  ProdutosService,
  SessionService,
} from "../../../../services";
import { responseErros } from "../../../../utils";
import impressaoEtiquetaValidator from "../middlewares/impressao-etiqueta.validator";
import ImpressaoEtiquetaStepper from "./impressao-etiqueta-stepper.component";
import filtroImpressaoValidator from "../middlewares/filtro-impressao-etiqueta.validator";

const etiquetasService = new EtiquetasService();
const produtosService = new ProdutosService();
const sessionService = new SessionService();

const ImpressaoEtiquetasDialog = ({ openDialog, setOpenDialog }) => {
  const [filtroImpressao, setFiltroImpressao] = useState({
    tipoFiltro: "produto",
  });
  const [produtos, setProdutos] = useState([]);
  const [produtoSelecionado, setProdutoSelecionado] = useState(null);
  const [listaOpcoesProdutos, setListaOpcoesProdutos] = useState([]);
  const [etiqueta, setEtiqueta] = useState(null);
  const [activeStep, setActiveStep] = useState(0);
  const [nfEntrada, setNfEntrada] = useState(null);
  const [listaOpcoesNfEntrada, setListaOpcoesNfEntrada] = useState([]);
  const [listaEtiquetas, setListaEtiquetas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputErro, setInputErro] = useState([]);
  const [configuracoes, setConfiguracoes] = useState({});
  const dadosEmpresa = sessionService.getEmpresaAndUsuario();
  const inputErros = new InputErros(inputErro, setInputErro);

  const steps = [
    { titulo: "Filtro", funcaoAcao: buscarProdutos },
    { titulo: "Confirmação", funcaoAcao: print },
  ];

  useEffect(() => {
    buscarConfiguracao();
  }, []);

  const buscarConfiguracao = () => {
    if (dadosEmpresa.empresa?.configuracoes) {
      const data = dadosEmpresa.empresa.configuracoes;
      setConfiguracoes(data);
    }
  };

  const validacaoModeloEtiqueta = (produtos) => {
    const codBar = etiqueta.layout.find(
      ({ elemento }) => elemento === "CODBAR"
    );
    if (codBar) {
      const filtroCodigoBarra = produtos.filter(
        ({ codigoBarra }) => !codigoBarra
      );
      return filtroCodigoBarra.length === 0;
    }
    return true;
  };

  async function buscarProdutos() {
    setLoading(true);
    return filtroImpressaoValidator
      .validate(
        { ...filtroImpressao, etiquetaId: etiqueta?.id },
        { abortEarly: false }
      )
      .then(async () => {
        let produtos = [];
        if (filtroImpressao.tipoFiltro === "produto") {
          produtos = [
            {
              ...produtoSelecionado,
              precoVenda: produtoSelecionado.informacao.precoVenda,
              checked: true,
            },
          ];
        } else {
          let filtros = {};
          if (filtroImpressao.tipoFiltro === "nfEntrada") {
            const produtosId = nfEntrada.nfEntradaItens.map(
              ({ produtoId }) => produtoId
            );
            filtros = { id: produtosId };
          } else {
            filtros = { ...filtroImpressao };
          }
          const filtrosTemp = {
            ...filtros,
            restrititva: true,
            nonPaginated: true,
          };
          const success = await produtosService
            .getAll(filtrosTemp)
            .then((res) => {
              if (!res.isAxiosError) {
                let result = res.data;
                if (result.length > 0) {
                  result = res.data.map((item) => ({
                    ...item,
                    precoVenda: item.informacao.precoVenda,
                    checked: true,
                  }));
                  produtos = result;
                  return true;
                }
                return false;
              }
              return false;
            });
          if (!success) {
            notification.alertaGenericos(
              "Não foi encontrado produtos para este filtro"
            );
            setLoading(false);
            return false;
          }
        }
        const validacao = validacaoModeloEtiqueta(produtos);
        if (!validacao) {
          const confirm = await handleConfirmacaoModeloEtiqueta();
          if (!confirm) {
            setLoading(false);
            return false;
          }
        }
        setProdutos(produtos);
        inputErros.limpar();
        setLoading(false);
        return true;
      })
      .catch((err) => {
        setLoading(false);
        inputErros.set(err);
        return false;
      });
  }

  const handleConfirmacaoModeloEtiqueta = async () => {
    const retornoAlerta = await notification.confirmacao(
      "Aviso",
      "Há produto(s) selecionado(s) sem o cadastro de código de barras. Deseja continuar?"
    );

    return retornoAlerta.isConfirmed;
  };

  async function print() {
    setLoading(true);
    const produtosParaValidacao = produtos.map(
      ({ id, quantidade, checked }) => ({
        produtoId: id,
        quantidade,
        checked,
      })
    );
    return impressaoEtiquetaValidator
      .validate(produtosParaValidacao, { abortEarly: false })
      .then(async () => {
        const produtosFiltrados = produtosParaValidacao.filter(
          ({ checked }) => checked
        );
        const result = await etiquetasService.buscarModeloCustomizado(
          etiqueta.id,
          produtosFiltrados
        );
        if (!result.isAxiosError) {
          const formData = new FormData();
          const buffer = new Uint8Array(result.data.data);
          const blob = new Blob([buffer], { type: "application/octet-stream" });
          formData.append("files", blob);
          formData.append(
            "printerName",
            configuracoes?.impressoraEtiqueta || null
          );
          const resultImpressao = await etiquetasService.imprimir(formData);
          if (!resultImpressao.isAxiosError) {
            notification.sucessoGenericos("Impressão realizada com sucesso!");
            setLoading(false);
            inputErros.limpar();
            return true;
          } else {
            responseErros(resultImpressao);
          }
        } else {
          responseErros(result);
        }
        setLoading(false);
        inputErros.limpar();
      })
      .catch((err) => {
        setLoading(false);
        inputErros.set(err);
        return false;
      });
  }

  const handleFinishDialog = () => {
    setFiltroImpressao({ tipoFiltro: "produto" });
    setActiveStep(0);
    setProdutos([]);
    setProdutoSelecionado([]);
    setListaOpcoesProdutos([]);
    setListaEtiquetas([]);
    inputErros.limpar();
    setEtiqueta(null);
    setNfEntrada(null);
    setOpenDialog(false);
  };

  const handleAvancarStep = async () => {
    const result = await steps[activeStep].funcaoAcao();
    if (result) {
      if (activeStep !== steps.length - 1) {
        setActiveStep((prevState) => prevState + 1);
      }
    }
  };

  const handleVoltarStep = () => {
    if (activeStep === 0) {
      handleFinishDialog();
    } else {
      setProdutos([]);
      setActiveStep((prevState) => prevState - 1);
    }
  };

  function renderAvancarButton() {
    const icon = activeStep === 0 ? "ph-caret-right" : "ph-check-circle";
    const label = activeStep === 0 ? "Próximo" : "Imprimir";
    return (
      <Button variant="contained" color="primary" onClick={handleAvancarStep}>
        <i
          className={`ph-fill ${icon}`}
          style={{ color: "#fff", fontSize: 22, marginRight: 10 }}
        ></i>
        {label}
      </Button>
    );
  }

  function renderVoltarButton() {
    const icon = activeStep === 0 ? "ph-x-circle" : "ph-caret-left";
    const label = activeStep === 0 ? "Fechar" : "Voltar";
    return (
      <Button variant="contained" onClick={handleVoltarStep}>
        <i
          className={`ph-fill ${icon}`}
          style={{ color: "#fff", fontSize: 22, marginRight: 10 }}
        ></i>
        {label}
      </Button>
    );
  }

  return (
    <>
      <Dialog
        maxWidth={activeStep === 0 ? "md" : "ls"}
        fullWidth
        open={openDialog}
        onClose={() => setOpenDialog(false)}
      >
        <DialogContent>
          <CardHeader title="Impressão de Etiquetas" />
          <CardContent>
            <Stepper activeStep={activeStep}>
              {steps.map(({ titulo }) => (
                <Step key={titulo}>
                  <StepLabel>{titulo}</StepLabel>
                </Step>
              ))}
            </Stepper>
            <ImpressaoEtiquetaStepper
              activeStep={activeStep}
              nfEntrada={nfEntrada}
              setNfEntrada={setNfEntrada}
              listaOpcoesNfEntrada={listaOpcoesNfEntrada}
              setListaOpcoesNfEntrada={setListaOpcoesNfEntrada}
              etiqueta={etiqueta}
              setEtiqueta={setEtiqueta}
              listaEtiquetas={listaEtiquetas}
              setListaEtiquetas={setListaEtiquetas}
              produtos={produtos}
              setProdutos={setProdutos}
              produtoSelecionado={produtoSelecionado}
              setProdutoSelecionado={setProdutoSelecionado}
              listaOpcoesProdutos={listaOpcoesProdutos}
              setListaOpcoesProdutos={setListaOpcoesProdutos}
              filtroImpressao={filtroImpressao}
              setFiltroImpressao={setFiltroImpressao}
              inputErros={inputErros}
            />
          </CardContent>
        </DialogContent>
        <DialogActions
          sx={{
            overflow: "auto",
            position: "sticky",
            bottom: 0,
            margin: 1,
            backgroundColor: "white",
            justifyContent: "flex-start",
            marginLeft: 4,
          }}
        >
          {renderVoltarButton()}
          {renderAvancarButton()}
        </DialogActions>
      </Dialog>
      <Loading loading={loading} />
    </>
  );
};

export default ImpressaoEtiquetasDialog;
